/**
 * 请求缓存工具
 * 用于缓存 API 请求的结果，避免重复请求，提高系统性能
 */

/**
 * 缓存项接口
 */
export interface CacheItem<T = any> {
  data: T;
  timestamp: number;
  expiration: number;
}

/**
 * 请求缓存配置
 */
export interface RequestCacheConfig {
  enabled?: boolean;
  expiration?: number; // 缓存过期时间（毫秒），默认 5 分钟
  maxSize?: number; // 最大缓存数量，默认 100
}

/**
 * 请求缓存工具类
 */
export class RequestCache {
  private cache: Map<string, CacheItem>;
  private config: Required<RequestCacheConfig>;

  constructor(config: RequestCacheConfig = {}) {
    this.cache = new Map();
    this.config = {
      enabled: config.enabled ?? true,
      expiration: config.expiration ?? 5 * 60 * 1000, // 默认 5 分钟
      maxSize: config.maxSize ?? 100, // 默认 100 个缓存项
    };
  }

  /**
   * 生成缓存键
   * @param url 请求 URL
   * @param params 请求参数
   * @param method 请求方法
   * @returns 缓存键
   */
  private generateKey(url: string, params?: any, method: string = 'GET'): string {
    const keyParts = [method.toUpperCase(), url];
    if (params) {
      try {
        const sortedParams = this.sortObject(params);
        keyParts.push(JSON.stringify(sortedParams));
      } catch {
        // 如果参数无法序列化，忽略参数部分
      }
    }
    return keyParts.join('|');
  }

  /**
   * 排序对象键，确保相同参数生成相同的缓存键
   * @param obj 要排序的对象
   * @returns 排序后的对象
   */
  private sortObject(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.sortObject(item));
    }

    const sorted: any = {};
    Object.keys(obj).sort().forEach(key => {
      sorted[key] = this.sortObject(obj[key]);
    });
    return sorted;
  }

  /**
   * 检查缓存是否过期
   * @param item 缓存项
   * @returns 是否已过期
   */
  private isExpired(item: CacheItem): boolean {
    return Date.now() - item.timestamp > item.expiration;
  }

  /**
   * 清理过期缓存
   */
  private cleanupExpired(): void {
    for (const [key, item] of this.cache.entries()) {
      if (this.isExpired(item)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * 检查缓存大小，如果超过最大值则清理最旧的缓存
   */
  private checkSize(): void {
    if (this.cache.size > this.config.maxSize) {
      // 获取所有缓存键并按时间戳排序
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      // 删除最旧的缓存，直到缓存大小符合要求
      const toDelete = entries.slice(0, this.cache.size - this.config.maxSize);
      toDelete.forEach(([key]) => {
        this.cache.delete(key);
      });
    }
  }

  /**
   * 获取缓存数据
   * @param url 请求 URL
   * @param params 请求参数
   * @param method 请求方法
   * @returns 缓存的数据，如果不存在或已过期则返回 null
   */
  get<T = any>(url: string, params?: any, method: string = 'GET'): T | null {
    if (!this.config.enabled) {
      return null;
    }

    // 清理过期缓存
    this.cleanupExpired();

    const key = this.generateKey(url, params, method);
    const item = this.cache.get(key);

    if (!item || this.isExpired(item)) {
      if (item) {
        this.cache.delete(key);
      }
      return null;
    }

    return item.data as T;
  }

  /**
   * 设置缓存数据
   * @param url 请求 URL
   * @param data 要缓存的数据
   * @param params 请求参数
   * @param method 请求方法
   * @param expiration 缓存过期时间（毫秒），如果不指定则使用默认值
   */
  set<T = any>(url: string, data: T, params?: any, method: string = 'GET', expiration?: number): void {
    if (!this.config.enabled) {
      return;
    }

    const key = this.generateKey(url, params, method);
    const cacheExpiration = expiration ?? this.config.expiration;

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiration: cacheExpiration,
    });

    // 检查缓存大小
    this.checkSize();
  }

  /**
   * 删除缓存
   * @param url 请求 URL
   * @param params 请求参数
   * @param method 请求方法
   */
  delete(url: string, params?: any, method: string = 'GET'): void {
    const key = this.generateKey(url, params, method);
    this.cache.delete(key);
  }

  /**
   * 清空所有缓存
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * 获取缓存大小
   * @returns 缓存项数量
   */
  getSize(): number {
    return this.cache.size;
  }

  /**
   * 启用缓存
   */
  enable(): void {
    this.config.enabled = true;
  }

  /**
   * 禁用缓存
   */
  disable(): void {
    this.config.enabled = false;
  }

  /**
   * 更新缓存配置
   * @param config 新的缓存配置
   */
  updateConfig(config: RequestCacheConfig): void {
    this.config = {
      ...this.config,
      ...config,
    };
  }
}

// 创建默认的请求缓存实例
export const requestCache = new RequestCache();

/**
 * 缓存装饰器，用于缓存函数调用结果
 * @param expiration 缓存过期时间（毫秒）
 * @returns 装饰器函数
 */
export function cacheable(expiration?: number) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;

    descriptor.value = async function (...args: any[]) {
      // 生成缓存键
      const key = `${propertyKey}|${JSON.stringify(args)}`;
      
      // 尝试从缓存获取
      const cached = requestCache.get(key);
      if (cached) {
        return cached;
      }

      // 调用原始方法
      const result = await originalMethod.apply(this, args);

      // 缓存结果
      requestCache.set(key, result, undefined, 'CACHE', expiration);

      return result;
    };

    return descriptor;
  };
}